import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthContext } from '../contexts/AuthContext';
import { usePortfolioContext } from '../contexts/PortfolioContext';
// DISABLED: Tour functionality hidden until ready to enable
// import { useOnboardingContext } from '../contexts/OnboardingContext';
import PortfolioManagementModal from './PortfolioManagementModal';
import { useTheme } from './ThemeProvider';
import { FaSun, FaMoon } from 'react-icons/fa';
import toast from 'react-hot-toast';
import { isStockNavRouteActive, isStocksRoute, STOCK_NAV_ROUTES } from '../constants/routes';

const NavigationDropdown = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showPortfolioManager, setShowPortfolioManager] = useState(false);

  const { user, signOut } = useAuthContext();
  const { currentPortfolio } = usePortfolioContext();
  // DISABLED: Tour functionality hidden until ready to enable
  // const { resetTourStatus } = useOnboardingContext();
  const { darkMode, toggleDarkMode } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isStocksMode = isStocksRoute(location.pathname);

  const mainRoutes = isStocksMode
    ? STOCK_NAV_ROUTES
    : [
        { path: '/', label: 'Dashboard', icon: '📊' },
        { path: '/positions', label: 'Positions', icon: '💼' },
        { path: '/allocation-planner', label: 'Planner', icon: '📈' },
        { path: '/transactions', label: 'Transactions', icon: '📝' },
        { path: '/strategies', label: 'Strategies', icon: '📊' },
        { path: '/imports', label: 'Tasks', icon: '⬆️' },
      ];

  const manageRoutes = isStocksMode
    ? []
    : [
        { path: '/groups', label: 'Groups', icon: '🗂️' },
        { path: '/tags', label: 'Tags', icon: '🏷️' },
        { path: '/lending-check', label: 'Lending', icon: '🏦' },
        { path: '/settings/notifications', label: 'Notification Settings', icon: '🔔' },
        { path: '/settings/security', label: 'Security', icon: '🔐' },
        { path: '/settings/api-keys', label: 'API Keys', icon: '🔑' },
      ];

  const handleNavigate = (path) => {
    navigate(path);
    setIsOpen(false);
  };

  const handleSignOut = async () => {
    const { error } = await signOut();
    if (error) {
      toast.error('Error signing out');
    } else {
      toast.success('Signed out successfully');
    }
    setIsOpen(false);
  };

  const handleThemeToggle = () => {
    toggleDarkMode();
    setIsOpen(false);
  };

  // DISABLED: Tour functionality hidden until ready to enable
  // const handleTakeTour = () => {
  //   resetTourStatus();
  //   setIsOpen(false);
  // };

  // const currentRoute = routes.find(route => route.path === location.pathname);

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex h-9 items-center gap-2 px-2.5 sm:px-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors shadow-sm"
        aria-label="Open navigation menu"
      >
        <svg className="w-5 h-5 text-gray-700 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7h16M4 12h16M4 17h16" />
        </svg>
        <span className="hidden sm:inline text-sm font-medium text-gray-700 dark:text-gray-300">
          Menu
        </span>
        <svg
          className={`w-4 h-4 text-gray-500 transition-transform ${
            isOpen ? 'rotate-180' : ''
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />
          
          {/* Dropdown */}
          <div className="absolute right-0 mt-2 w-[calc(100vw-1rem)] max-w-80 max-h-[calc(100vh-5rem)] overflow-y-auto overscroll-contain bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50">
            <div className="py-1">
              {/* Portfolio Section: show selected and allow change */}
              <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                <div className="text-xs font-semibold text-neutral-600 dark:text-neutral-400 uppercase tracking-wide mb-2">
                  Portfolio
                </div>
                <div className="flex items-center justify-between">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                      {currentPortfolio?.name || 'No portfolio selected'}
                    </div>
                  </div>
                  <button
                    onClick={() => { setIsOpen(false); setShowPortfolioManager(true); }}
                    className="ml-3 px-3 py-1.5 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700"
                  >
                    Change
                  </button>
                </div>
              </div>

              {/* Main (only on small screens; header already has these) */}
              {mainRoutes.length > 0 && (
                <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 md:hidden">
                  <div className="text-xs font-semibold text-neutral-600 dark:text-neutral-400 uppercase tracking-wide mb-2">Navigate</div>
                  <div className="space-y-1">
                    {mainRoutes.map((route) => {
                      const isActive = isStocksMode
                        ? isStockNavRouteActive(route.path, location.pathname)
                        : location.pathname === route.path;
                      return (
                      <button
                        key={route.path}
                        onClick={() => handleNavigate(route.path)}
                        className={`w-full text-left px-3 py-2 text-sm rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center gap-3 ${
                          isActive
                            ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                            : 'text-gray-700 dark:text-gray-300'
                        }`}
                      >
                        <span>{route.label}</span>
                        {isActive && (
                          <svg className="w-4 h-4 text-blue-500 ml-auto" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Management (always visible) */}
              {manageRoutes.length > 0 && (
                <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                  <div className="text-xs font-semibold text-neutral-600 dark:text-neutral-400 uppercase tracking-wide mb-2">Manage</div>
                  <div className="space-y-1">
                    {manageRoutes.map((route) => (
                      <button
                        key={route.path}
                        onClick={() => handleNavigate(route.path)}
                        className={`w-full text-left px-3 py-2 text-sm rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center gap-3 ${
                          location.pathname === route.path
                            ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                            : 'text-gray-700 dark:text-gray-300'
                        }`}
                      >
                        <span>{route.label}</span>
                        {location.pathname === route.path && (
                          <svg className="w-4 h-4 text-blue-500 ml-auto" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Theme Toggle Section */}
              <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                <button
                  onClick={handleThemeToggle}
                  className="w-full text-left px-3 py-2 text-sm rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center space-x-3 text-gray-700 dark:text-gray-300"
                >
                  {darkMode ? (
                    <FaSun className="w-4 h-4 text-yellow-500" />
                  ) : (
                    <FaMoon className="w-4 h-4 text-blue-700" />
                  )}
                  <span>{darkMode ? 'Light Mode' : 'Dark Mode'}</span>
                </button>
{/* DISABLED: Tour button hidden until ready to enable
                <button
                  onClick={handleTakeTour}
                  className="w-full text-left px-3 py-2 text-sm rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center space-x-3 text-gray-700 dark:text-gray-300"
                >
                  <span className="text-base">🎯</span>
                  <span>Take the tour</span>
                </button>
*/}
              </div>

          {/* <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
            <button
              onClick={() => {
                if (isOnboarding) {
                  disableOnboarding();
                  navigate('/');
                } else {
                  enableOnboarding();
                  navigate('/onboarding');
                }
                setIsOpen(false);
              }}
              className={`w-full text-left px-3 py-2 text-sm rounded-md transition-colors flex items-center space-x-3 ${
                isOnboarding ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              <span className="text-base">🧪</span>
              <span>{isOnboarding ? 'Disable Onboarding' : 'Enable Onboarding'}</span>
            </button>
          </div> */}

              {/* User Profile Section */}
              {user && (
                <div className="px-4 py-3">
                  <div className="text-xs font-semibold text-neutral-600 dark:text-neutral-400 uppercase tracking-wide mb-2">
                    Account
                  </div>
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm font-medium">
                      {user.email?.charAt(0).toUpperCase() || 'U'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        Signed in as
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                        {user.email}
                      </p>
                    </div>
                  </div>
                  <div className="space-y-1 mb-3">
                    {/* Notification Settings moved to Manage section */}
                  </div>
                  <button
                    onClick={handleSignOut}
                    className="w-full text-left px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors flex items-center space-x-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                    <span>Sign out</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Search Modal */}

      {showPortfolioManager && (
        <PortfolioManagementModal
          isOpen={showPortfolioManager}
          onClose={() => setShowPortfolioManager(false)}
        />
      )}

    </div>
  );
};

export default NavigationDropdown; 